import { CommonModule } from "@angular/common";
import {
  Component,
  computed,
  inject,
  input,
  signal,
  TemplateRef,
  viewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AccountMarketplace, Strategy, StrategyAsin } from "@front/m19-api-client";
import {
  AccountSelectionService,
  AsinService,
  SbStrategyService,
  TacosStrategiesService,
  TacosStrategy,
} from "@front/m19-services";
import {
  IAlertComponent,
  IButtonDirective,
  IIconComponent,
  ModalRef,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { AsinSelectionComponent } from "@m19-board/shared/asin-selection/asin-selection.component";
import {
  CreativeSbAsinsResult,
  TacosSbAsinsCheckComponent,
  TacosSbAsinsCheckData,
} from "@m19-board/shared/tacos-sb-asins-check/tacos-sb-asins-check.component";
import { SpinnerComponent } from "@front/m19-ui";
import { forkJoin, Observable, switchMap, take } from "rxjs";
import { StrategyService } from "@front/m19-injection-token";

@Component({
  selector: "app-tacos-products",
  imports: [
    CommonModule,
    IAlertComponent,
    TranslocoDirective,
    IButtonDirective,
    IIconComponent,
    SpinnerComponent,
    AsinSelectionComponent,
  ],
  templateUrl: "./tacos-products.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TacosProductsComponent {
  private readonly accountSelection = inject(AccountSelectionService);
  private readonly asinService = inject(AsinService);
  private readonly tacosStrategiesService = inject(TacosStrategiesService);
  private readonly modalService = inject(ModalService);
  private readonly translocoService = inject(TranslocoService);
  private readonly strategyService = inject(StrategyService);
  private readonly toastrService = inject(ToasterService);
  private readonly sbStrategyService = inject(SbStrategyService);
  tacosStrategy = input.required<TacosStrategy>();
  sbAsins = input.required<StrategyAsin[] | undefined>();
  am = input.required<AccountMarketplace>();
  isReadOnly = input.required<boolean>();
  missingAsinsInSb = input<string[] | undefined>();

  addAsinsModal = viewChild<TemplateRef<any>>("addAsinsModal");
  addAsinsModalRef: ModalRef<StrategyAsin[]> | undefined;

  addProductLoading = signal(false);
  addProductSelection = signal<string[]>([]);

  deleteProductSelection = signal<string[]>([]);
  deleteProductLoading = signal(false);

  asinList = computed(() => this.tacosStrategy()?.spStrategy?.asins?.map((a) => a.asin) ?? []);

  unavailableAsins = toSignal<Set<string>>(this.tacosStrategiesService.unavailableAsinsForTacos$);

  catalog = toSignal(
    this.accountSelection.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.asinService.getCatalog(am.accountId, am.marketplace)),
    ),
  );

  productAvailableToAdd = computed<string[]>(() => {
    if (!this.catalog() || !this.unavailableAsins()) return [];
    return Array.from(this.catalog()?.asinOffers.keys() ?? []).filter((asin) => !this.unavailableAsins()?.has(asin));
  });

  ineligibleAsins = computed<string[]>(() => {
    if (!this.catalog() || !this.tacosStrategy()?.spStrategy?.asins) return [];
    const eligibility = this.catalog()!.getSPEligibility();
    return this.tacosStrategy()!
      .spStrategy!.asins!.filter((asin) => !eligibility.get(asin.asin!)?.status)
      .map((asin) => asin.asin!);
  });

  openAddAsinsModal() {
    this.addAsinsModalRef = this.modalService.openModal(this.addAsinsModal()!, {
      modalTitle: `${this.translocoService.translate("product-group-page.edit_products")} - ${this.tacosStrategy()?.spStrategy?.name}`,
      size: "xl",
      height: "fill",
    });
  }

  addAsinsToTacos(asins: string[]) {
    const addAsinsCalls = [this.strategyService.addAsinsToStrategy(this.tacosStrategy()!.spStrategy!, asins)];
    if (this.tacosStrategy()?.sdStrategy) {
      addAsinsCalls.push(this.strategyService.addAsinsToStrategy(this.tacosStrategy()!.sdStrategy!, asins));
    }

    this.addProductLoading.set(true);
    forkJoin(addAsinsCalls).subscribe(() => {
      this.toastrService.success(
        `${asins.length} ${this.translocoService.translate("tacos-strategy-page.asins_successfully_added")}`,
      );
      this.addProductLoading.set(false);
      this.addAsinsModalRef?.close();
      this.addProductSelection.set([]);
    });
  }

  removeAsinsFromTacos(asins: string[]) {
    const removeAsinsCalls = [this.strategyService.deleteAsinsFromStrategy(this.tacosStrategy()!.spStrategy!, asins)];
    if (this.tacosStrategy()?.sdStrategy) {
      removeAsinsCalls.push(this.strategyService.deleteAsinsFromStrategy(this.tacosStrategy()!.sdStrategy!, asins));
    }

    this.deleteProductLoading.set(true);
    let removeCall: Observable<Strategy[]> | undefined;
    if (this.tacosStrategy()?.sbStrategy && this.sbAsins()?.some((asin) => asins.includes(asin.asin!))) {
      this.addAsinsModalRef?.close();

      const ref = this.modalService.openModal<TacosSbAsinsCheckData, Array<CreativeSbAsinsResult>>(
        TacosSbAsinsCheckComponent,
        {
          size: "sm",
          height: "fill",
          modalTitle: this.translocoService.translate("tacos-strategy.impacted_ad_lines_title"),
          data: {
            sbStrategyId: this.tacosStrategy()!.sbStrategy!.strategyId!,
            asinsToRemove: asins,
          },
        },
      );

      removeCall = ref.afterClosed().pipe(
        take(1),
        switchMap((result) =>
          forkJoin(
            result.map((creative) =>
              this.sbStrategyService.updateSbCreativeClusterAsync(creative.creative, creative.asins),
            ),
          ),
        ),
        switchMap(() => forkJoin(removeAsinsCalls)),
      );
    } else {
      removeCall = forkJoin(removeAsinsCalls);
    }

    removeCall.subscribe(() => {
      this.toastrService.success(
        `${asins.length} ${this.translocoService.translate("tacos-strategy-page.asins_successfully_removed")}`,
      );

      this.deleteProductSelection.set([]);
      this.deleteProductLoading.set(false);
    });
  }
}
